import type { Role, Tier } from "@/lib/api/client";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 20;
const MAX_PAGE_SIZE = 100;

/** Filter and pagination state for the admin users list. */
export type UserFilters = {
  query: string;
  tier: Tier | null;
  role: Role | null;
  isActive: boolean | null;
  page: number;
  pageSize: number;
};

export const DEFAULT_USER_FILTERS: UserFilters = {
  query: "",
  tier: null,
  role: null,
  isActive: null,
  page: DEFAULT_PAGE,
  pageSize: DEFAULT_PAGE_SIZE,
};

const TIER_VALUES = new Set<string>(["free", "premium"]);
const ROLE_VALUES = new Set<string>(["user", "admin"]);

function isTier(value: string): value is Tier {
  return TIER_VALUES.has(value);
}

function isRole(value: string): value is Role {
  return ROLE_VALUES.has(value);
}

function parsePositiveInt(value: string | null, fallback: number): number {
  if (value === null || value.length === 0) {
    return fallback;
  }
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed) || parsed < 1) {
    return fallback;
  }
  return parsed;
}

export function parseIsActiveParam(value: string | null): boolean | null {
  if (value === "true") {
    return true;
  }
  if (value === "false") {
    return false;
  }
  return null;
}

/** Parse a tier filter value from a Select; empty string means no filter. */
export function parseTierFilter(value: string | null): Tier | null {
  if (value === null || value.length === 0) {
    return null;
  }
  return isTier(value) ? value : null;
}

/** Parse a role filter value from a Select; empty string means no filter. */
export function parseRoleFilter(value: string | null): Role | null {
  if (value === null || value.length === 0) {
    return null;
  }
  return isRole(value) ? value : null;
}

/** Parse URL search params into user list filters. */
export function parseUserFilters(searchParams: URLSearchParams): UserFilters {
  const tierParam = searchParams.get("tier");
  const roleParam = searchParams.get("role");
  const isActiveParam = searchParams.get("is_active");

  const page = parsePositiveInt(searchParams.get("page"), DEFAULT_PAGE);
  const rawPageSize = parsePositiveInt(searchParams.get("page_size"), DEFAULT_PAGE_SIZE);
  const pageSize = Math.min(rawPageSize, MAX_PAGE_SIZE);

  return {
    query: searchParams.get("query") ?? "",
    tier: tierParam !== null && isTier(tierParam) ? tierParam : null,
    role: roleParam !== null && isRole(roleParam) ? roleParam : null,
    isActive: parseIsActiveParam(isActiveParam),
    page,
    pageSize,
  };
}

/** Serialize user list filters to URL search params (omits default values). */
export function userFiltersToSearchParams(filters: UserFilters): URLSearchParams {
  const params = new URLSearchParams();

  if (filters.query.length > 0) {
    params.set("query", filters.query);
  }
  if (filters.tier !== null) {
    params.set("tier", filters.tier);
  }
  if (filters.role !== null) {
    params.set("role", filters.role);
  }
  if (filters.isActive !== null) {
    params.set("is_active", String(filters.isActive));
  }
  if (filters.page !== DEFAULT_PAGE) {
    params.set("page", String(filters.page));
  }
  if (filters.pageSize !== DEFAULT_PAGE_SIZE) {
    params.set("page_size", String(filters.pageSize));
  }

  return params;
}
